import { ChevronsLeft, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { DocumentList } from './DocumentList'
import { LibraryList } from './LibraryList'
import type { ActiveNav } from './Sidebar'
import type { Draft, LibraryItem } from '@/data/mock'

interface SubSidebarProps {
  activeNav: ActiveNav
  onClose: () => void
  documents: Draft[]
  activeDocId: string | null
  onDocSelect: (id: string) => void
  libraryItems: LibraryItem[]
  supersededItems?: LibraryItem[]
  onUploadClick: () => void
  onOpenDetails?: (id: string) => void
  onDeleteItem?: (id: string) => void
  onToggleRag?: (id: string, isActive: boolean) => void
  activeWorkspaceId: string | null
  sourceIdsWithClaims?: Set<string>
  auditingDraftIds?: Set<string>
}

export function SubSidebar({
  activeNav,
  onClose,
  documents,
  activeDocId,
  onDocSelect,
  libraryItems,
  supersededItems,
  onUploadClick,
  onOpenDetails,
  onDeleteItem,
  onToggleRag,
  activeWorkspaceId,
  sourceIdsWithClaims,
  auditingDraftIds,
}: SubSidebarProps) {
  if (!activeNav) return null

  const title = activeNav === 'documents' ? 'Documents' : 'Library'

  return (
    <aside className="w-[280px] shrink-0 bg-gray-50/50 border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <div className="flex items-center gap-1">
          {activeNav === 'library' && (
            <Tooltip open={activeWorkspaceId ? false : undefined}>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7${!activeWorkspaceId ? ' opacity-50' : ''}`}
                    onClick={onUploadClick}
                    disabled={!activeWorkspaceId}
                  >
                    <Plus className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Please select or create a workspace first</TooltipContent>
            </Tooltip>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClose}
          >
            <ChevronsLeft className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden py-2">
        {activeNav === 'documents' && (
          <DocumentList
            documents={documents}
            activeDocId={activeDocId}
            onDocSelect={onDocSelect}
            auditingDraftIds={auditingDraftIds}
          />
        )}
        {activeNav === 'library' && (
          <LibraryList items={libraryItems} supersededItems={supersededItems} onOpenDetails={onOpenDetails} onDeleteItem={onDeleteItem} onToggleRag={onToggleRag} sourceIdsWithClaims={sourceIdsWithClaims} />
        )}
      </div>
    </aside>
  )
}
